import {
  CSSProperties,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useState
} from 'react';
import { KEY_BOARD } from 'constants/general';
import { TAB_INDEX } from 'constants/attrib';
import { ChevronRight } from 'assets/icons/ChevronRight';

interface ExpandableSectionProps {
  title?: string | JSX.Element;
  open?: boolean;
  wrapperStyles?: string;
  icon?: JSX.Element;
  onHeaderClick?: () => void;
  headerTitleStyles?: string;
  defaultOpen?: boolean;
  showDropdownIcon?: boolean;
  /** Actions rendered before the chevron; clicks should stopPropagation. */
  headerActions?: ReactNode;
  /** Pin header while the nearest scroll parent scrolls (e.g. modal body). */
  stickyHeader?: boolean;
  headerStyle?: CSSProperties;
  bodyStyle?: CSSProperties;
  headerClassName?: string;
  bodyClassName?: string;
  style?: CSSProperties;
}

const getRootOverflowClass = (stickyHeader: boolean): string =>
  stickyHeader === true ? 'overflow-visible' : 'overflow-hidden';

const getHeaderStickyClass = (stickyHeader: boolean): string =>
  stickyHeader === true
    ? 'sticky top-0 z-20 shadow-sm bg-white/85 dark:bg-secondary-dark-100/85 backdrop-blur-md'
    : '';

const getOpenBodyClass = (stickyHeader: boolean): string =>
  stickyHeader === true
    ? '~p-2/4'
    : 'max-h-[2000px] overflow-y-auto scrollbar-thin scrollbar-thumb-primary-dark-400 ~p-2/4';

const getHeaderBaseBgClass = (stickyHeader: boolean): string =>
  stickyHeader === true ? '' : 'dark:bg-secondary-dark-100 bg-white';

const getHeaderInteractionClass = ({
  isActive,
  stickyHeader
}: {
  isActive: boolean;
  stickyHeader: boolean;
}): string => {
  if (isActive === true) {
    return 'bg-secondary-100/90 dark:bg-primary-dark-200/90';
  }
  if (stickyHeader === true) {
    return 'hover:bg-secondary-50/80 dark:hover:bg-secondary-dark-200/50';
  }
  return 'hover:bg-secondary-50 dark:hover:bg-secondary-dark-200/50 active:bg-secondary-100 dark:active:bg-primary-dark-200';
};
const ExpandableSection = ({
  title,
  children,
  open,
  wrapperStyles,
  icon,
  onHeaderClick,
  headerTitleStyles = 'font-medium ~text-sm/base',
  defaultOpen = false,
  showDropdownIcon = true,
  headerActions,
  stickyHeader = false,
  headerStyle,
  bodyStyle,
  headerClassName = '',
  bodyClassName = '',
  style
}: PropsWithChildren<ExpandableSectionProps>) => {
  const isControlled = open !== undefined;
  const [isOpen, setIsOpen] = useState(open ?? defaultOpen);
  const [isActive, setIsActive] = useState(false);
  const hasCustomHeaderBg = headerClassName.length > 0;

  useEffect(() => {
    if (isControlled === true) {
      setIsOpen(open ?? false);
    }
  }, [isControlled, open]);

  const handleToggle = () => {
    if (isControlled !== true) {
      setIsOpen((prev) => !prev);
    }
    onHeaderClick?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if ([KEY_BOARD.ENTER.KEY, KEY_BOARD.SPACE.KEY].includes(event.key)) {
      event.preventDefault();
      handleToggle();
    }
  };

  return (
    <div
      style={style}
      className={`w-full h-fit flex flex-col rounded-lg shadow-md dark:shadow-secondary-dark-400/30 dark:bg-secondary-dark-100 bg-white border border-secondary-400/60 dark:border-secondary-dark-400 ${getRootOverflowClass(stickyHeader)} ${wrapperStyles}`}>
      <div
        className={`shrink-0 w-full h-fit flex items-center justify-between ~p-2/3 transition-colors duration-150 border-b border-secondary-400/60 dark:border-secondary-dark-400 ${hasCustomHeaderBg ? '' : getHeaderBaseBgClass(stickyHeader)} ${getHeaderStickyClass(stickyHeader)} ${hasCustomHeaderBg ? '' : getHeaderInteractionClass({ isActive, stickyHeader })} ${headerClassName}`}
        style={headerStyle}
        role='button'
        tabIndex={TAB_INDEX.DEFAULT}
        onClick={(event) => {
          event.stopPropagation();
          handleToggle();
        }}
        onKeyDown={handleKeyDown}
        onTouchStart={() => setIsActive(true)}
        onTouchEnd={() => setTimeout(() => setIsActive(false), 150)}
        onMouseDown={() => setIsActive(true)}
        onMouseUp={() => setIsActive(false)}
        onMouseLeave={() => setIsActive(false)}>
        <h6 className={headerTitleStyles}>{title}</h6>
        <div className='flex items-center ~gap-1/2 shrink-0'>
          {headerActions}
          {showDropdownIcon && (
            <button
              type='button'
              className={`${
                isOpen ? 'rotate-90' : 'rotate-0'
              } transition-transform duration-300 ease-in-out ~w-4/5 ~h-4/5 text-primary-900 dark:text-secondary-100`}
              aria-label={isOpen ? 'Collapse section' : 'Expand section'}
              aria-expanded={isOpen}>
              {icon || (
                <ChevronRight
                  styles='~w-4/5 ~h-4/5 h-auto'
                  fill='currentColor'
                  fillDarkMode='currentColor'
                />
              )}
            </button>
          )}
        </div>
      </div>
      <div
        className={`w-full transition-all duration-300 ease-in-out ${
          isOpen === true
            ? getOpenBodyClass(stickyHeader)
            : 'max-h-0 overflow-hidden'
        } ${bodyClassName}`}
        style={{
          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
          ...bodyStyle
        }}>
        {isOpen === true ? (
          <div className='animate-fadeIn'>{children}</div>
        ) : null}
      </div>
    </div>
  );
};

export default ExpandableSection;
